import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tasksApi } from '@/services/api';

/** Fetches all task dependency rows for a project (task_id, depends_on_task_id). */
export const useDependencies = (projectId: number) =>
  useQuery({
    queryKey: ['dependencies', projectId],
    queryFn: async () => {
      const { data, error } = await tasksApi.getDependenciesByProject(projectId);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });

/** Fetch all task dependencies across multiple projects (for dashboard export). */
export const useAllDependencies = (projectIds: number[]) =>
  useQuery({
    queryKey: ['dependencies', 'all', projectIds],
    queryFn: async () => {
      const { data, error } = await tasksApi.getDependenciesByProjectIds(projectIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: projectIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

/** Mutation that batch-updates dates for multiple tasks atomically via RPC. */
export const useBatchUpdateTaskDates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      updates: Array<{ id: number; start_date: string; end_date: string }>
    ) => {
      const { error } = await tasksApi.batchUpdateDates(updates);
      if (error) throw error;
      return updates;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['dependencies'] });
    },
  });
};
